import React from "react";

const PrivacyPolicy = () => {
  const isApp =
    new URLSearchParams(window.location.search).get("source") === "app";

  const pageTitleClass = isApp
    ? "text-lg font-semibold"
    : "text-3xl font-semibold";

  const sectionTitleClass = isApp
    ? "text-sm font-semibold mb-2"
    : "text-lg font-semibold mb-3";

  const bodyTextClass = isApp
    ? "text-sm leading-6"
    : "text-base leading-relaxed";

  const subHeadingClass = isApp
    ? "text-sm font-semibold mt-4 mb-2 text-orange-600"
    : "text-xl font-semibold mt-6 mb-2 text-orange-600";

  return (
    <div className="bg-gray-50 text-gray-800 antialiased min-h-screen">

      {!isApp && (
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <h1 className={pageTitleClass}>
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Last updated: March 2026
            </p>
          </div>
        </header>
      )}

      <main
        className={`max-w-4xl mx-auto px-5 py-4 bg-white shadow-md rounded-2xl ${
          isApp ? "mt-3 mb-6" : "mt-2 mb-20"
        }`}
      >

        {/* 1 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>1. Introduction</h2>
          <p className={bodyTextClass}>
            Welcome to <strong>GreetIndia9 – Digital Invitations</strong>.
            We respect your privacy and are committed to protecting your personal
            information. This Privacy Policy explains how we collect, use,
            and safeguard your information when you use our mobile application
            and website.
          </p>
        </section>

        {/* 2 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>2. Information We Collect</h2>

          <ul className={`list-disc list-inside space-y-1 ${bodyTextClass}`}>
            <li><strong>Personal Information:</strong> Name, profile photo, mobile number.</li>
            <li><strong>Media Files:</strong> Photos uploaded for greeting personalization.</li>
            <li><strong>Usage Data:</strong> App interaction data and device information.</li>
            <li><strong>Technical Data:</strong> Log data for improving app performance.</li>
          </ul>
        </section>

        {/* 3 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>3. How We Use Your Information</h2>

          <ul className={`list-disc list-inside space-y-1 ${bodyTextClass}`}>
            <li>To create and personalize greeting cards.</li>
            <li>To provide invitation templates and services.</li>
            <li>To improve app performance and user experience.</li>
            <li>To maintain security and prevent misuse.</li>
          </ul>
        </section>

        {/* 4 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>4. Data Sharing</h2>

          <p className={bodyTextClass}>
            We do not sell, rent, or trade your personal information.
            Your data may only be shared with trusted service providers
            required to operate the app securely.
          </p>
        </section>

        {/* 5 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>5. App Permissions</h2>

          <ul className={`list-disc list-inside space-y-1 ${bodyTextClass}`}>
            <li><strong>Storage:</strong> To upload photos for invitations.</li>
            <li><strong>Camera:</strong> To capture photos for personalization.</li>
            <li><strong>Internet:</strong> To load templates and share greetings.</li>
          </ul>
        </section>

        {/* 6 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>6. Data Security</h2>

          <p className={bodyTextClass}>
            We implement appropriate technical and organizational measures
            to protect your personal data against unauthorized access,
            alteration, disclosure, or destruction.
          </p>
        </section>

        {/* 7 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>7. Data Retention</h2>

          <p className={bodyTextClass}>
            We retain personal information only for as long as necessary
            to provide our services. When data is no longer required,
            it is securely deleted.
          </p>
        </section>

        {/* 8 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>8. Account Deletion</h2>

          <p className={bodyTextClass}>
            Users may request deletion of their account and associated data
            at any time.
          </p>

          <p className={bodyTextClass}>
            To request account deletion, please email:
          </p>

          <p className={bodyTextClass}>
            <a
              href="mailto:greetindia9@gmail.com"
              className="text-orange-600 hover:underline"
            >
              greetindia9@gmail.com
            </a>
          </p>

          <p className={bodyTextClass}>
            Once requested, we will delete the account and related data
            within a reasonable time unless retention is required by law.
          </p>
        </section>

        {/* 9 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>9. Third-Party Services</h2>

          <p className={bodyTextClass}>
            Our app may use third-party services such as analytics,
            cloud storage, and infrastructure providers to improve
            functionality and performance. These services may collect
            limited technical data according to their privacy policies.
          </p>
        </section>

        {/* 10 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>10. Children's Privacy</h2>

          <p className={bodyTextClass}>
            Our services are not intended for children under the age of 13.
            We do not knowingly collect personal data from children.
          </p>
        </section>

        {/* 11 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>11. International Users</h2>

          <p className={bodyTextClass}>
            By using our services, you agree that your information may be
            processed and stored according to this Privacy Policy.
          </p>
        </section>

        {/* 12 */}
        <section className="mb-6">
          <h2 className={sectionTitleClass}>12. Changes to This Policy</h2>

          <p className={bodyTextClass}>
            We may update this Privacy Policy from time to time.
            Changes will be reflected on this page with an updated date.
          </p>
        </section>

        {/* 13 */}
        <section className="mb-8">
          <h2 className={sectionTitleClass}>13. Contact Us</h2>

          <p className={bodyTextClass}>
            <strong>GreetIndia9 – Digital Invitations</strong>
          </p>

          <p className={bodyTextClass}>
            Email:{" "}
            <a
              href="mailto:greetindia9@gmail.com"
              className="text-orange-600 hover:underline"
            >
              greetindia9@gmail.com
            </a>
          </p>

          <p className={bodyTextClass}>
            Bikaner, Rajasthan, India
          </p>
        </section>

        {/* Security */}
        <section>
          <h2 className={sectionTitleClass}>
            Security Measures
          </h2>

          {[
            "Secure data storage",
            "Encrypted communication",
            "Network security monitoring",
            "User privacy protection",
            "Periodic security audits",
          ].map((title, i) => (
            <h3 key={i} className={subHeadingClass}>
              {title}
            </h3>
          ))}
        </section>

      </main>
    </div>
  );
};

export default PrivacyPolicy;